import React, { useState } from 'react';
import { Box, Button, Typography, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText } from '@mui/material';
import { useEditMode } from './EditModeContext';

const SaveBar = () => {
  const { isEditMode, canEditData, hasChanges, isSaving, saveError, clearSaveError, isLive, changeDescriptions, saveChanges, discardChanges } = useEditMode();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  // Hide the save flow on statuses where the backend's PATCH /detail-data
  // would reject the save — admins on PENDING/RUNNING/NO_MATCH/FAILED keep
  // the action bar but can't save inline data edits.
  if (!isEditMode || !canEditData) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          backgroundColor: hasChanges ? '#1a1a2e' : '#f5f5f5',
          color: hasChanges ? '#fff' : '#666',
          borderTop: '1px solid #ddd',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="body2">
          {hasChanges
            ? `${changeDescriptions.length} change${changeDescriptions.length !== 1 ? 's' : ''} pending`
            : 'Edit mode — make changes to the verification data'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => hasChanges ? setShowDiscard(true) : discardChanges()}
            disabled={isSaving}
            sx={{
              color: hasChanges ? '#fff' : '#666',
              borderColor: hasChanges ? '#fff' : '#ccc',
              '&:hover': { borderColor: hasChanges ? '#ccc' : '#999' },
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => setShowConfirm(true)}
            disabled={!hasChanges || isSaving}
            sx={{ backgroundColor: '#4566EB' }}
          >
            {isSaving ? 'Saving...' : 'Save changes'}
          </Button>
        </Box>
      </Box>

      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm changes</DialogTitle>
        <DialogContent>
          {isLive && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This verification is <strong>COMPLETED</strong> and visible to the customer. Changes will be immediately visible.
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            The following changes will be saved:
          </Typography>
          <List dense>
            {changeDescriptions.map((desc, i) => (
              <ListItem key={i}>
                <ListItemText primary={desc} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setShowConfirm(false);
              await saveChanges();
            }}
            sx={{ backgroundColor: '#4566EB' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDiscard} onClose={() => setShowDiscard(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have {changeDescriptions.length} unsaved change{changeDescriptions.length !== 1 ? 's' : ''}. This will discard all edits.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscard(false)}>Keep editing</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => { setShowDiscard(false); discardChanges(); }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {saveError && (
        <Snackbar open autoHideDuration={6000} onClose={clearSaveError}>
          <Alert severity="error" onClose={clearSaveError}>{saveError}</Alert>
        </Snackbar>
      )}

      {/* Spacer to prevent content from being hidden behind the bar */}
      {isEditMode && <Box sx={{ height: 56 }} />}
    </>
  );
};

export default SaveBar;
